import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient } from '../lib/api';
import { Post, PostDocument } from '../types';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import toast from 'react-hot-toast';

const PostEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'detailed' as 'detailed' | 'texted' | 'imaged',
    status: 'draft' as 'draft' | 'scheduled' | 'published',
    image: '',
    scheduled_at: '',
    category_ids: [] as string[],
    manual_created_at: '',
    event_location: '',
    event_time: '',
    documents: [] as PostDocument[],
  });

  const documentInputRef = useRef<HTMLInputElement>(null);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<number | null>(null);
  const isAutoSavingRef = useRef(false);
  // Реальный id поста после первого автосохранения (чтобы не плодить дубликаты,
  // пока useParams ещё держит 'new')
  const createdPostIdRef = useRef<string | null>(null);
  // Флаг «создающий запрос сейчас в полёте» — блокирует повторные createPost,
  // пока первый не вернул id
  const isCreatingRef = useRef(false);

  // Функция для debounce автосохранения (тихий режим)
  const debouncedAutoSave = (data: any) => {
    // Очищаем предыдущий таймаут
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Устанавливаем новый таймаут на 3 секунды
    const timeout = setTimeout(() => {
      if (formData.status === 'draft' && data.title && data.content && data.content !== '<br>') {
        const postData = {
          title: data.title,
          content: data.content,
          type: data.type,
          status: data.status,
          image: data.image,
          category_ids: data.category_ids,
          scheduled_at: data.scheduled_at ? new Date(data.scheduled_at).toISOString() : undefined,
          manual_created_at: data.manual_created_at ? new Date(data.manual_created_at).toISOString() : undefined,
          event_location: data.event_location,
          event_time: data.event_time,
          documents: data.documents || [],
        };
        // Используем тихую мутацию для автосохранения
        autoSaveMutation.mutate(postData);
      }
    }, 3000);
    
    setAutoSaveTimeout(timeout);
  };

  // Загружаем категории для выбора
  const { data: categoriesData } = useQuery(
    ['categories'],
    () => apiClient.getCategories({ limit: 100 }),
    { staleTime: 60000 }
  );

  // При создании поста автоматически отмечать категорию по умолчанию, если она есть и доступна
  useEffect(() => {
    if (id === 'new' && categoriesData?.data && categoriesData.data.length > 0 && formData.category_ids.length === 0) {
      // Найти категорию по умолчанию среди доступных
      const defaultCategory = categoriesData.data.find((cat: any) => cat.is_default);
      if (defaultCategory) {
        setFormData(prev => ({ ...prev, category_ids: [defaultCategory.id] }));
      }
    }
    // Если нет категории по умолчанию, не выставлять ничего
  }, [id, categoriesData]);

  // Загружаем пост для редактирования
  const { data: postData, isLoading: isLoadingPost } = useQuery(
    ['post', id],
    () => apiClient.getPost(id!),
    {
      enabled: !!id && id !== 'new',
      onSuccess: (data) => {
        const post = data.data;
        setFormData({
          title: post.title,
          content: post.content,
          type: post.type,
          status: post.status,
          image: post.image || '',
          scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '',
          category_ids: post.categories.map((cat: any) => cat.id),
          manual_created_at: post.manual_created_at ? new Date(post.manual_created_at).toISOString().slice(0, 10) : '',
          event_location: post.event_location || '',
          event_time: post.event_time || '',
          documents: post.documents || [],
        });
      },
    }
  );

  // Гарантированно обновлять formData при изменении postData или id
  // Но только если это не автосохранение (не обновляем во время редактирования)
  useEffect(() => {
    if (postData && id && id !== 'new') {
      // Не обновляем formData если идет автосохранение
      if (isAutoSavingRef.current) {
        return;
      }
      
      const post = postData.data;
      // Обновляем только если контент действительно изменился на сервере
      // и это не результат автосохранения (проверяем, что контент отличается)
      const serverContent = post.content || '';
      const currentContent = formData.content || '';
      
      // Если контент совпадает, значит это результат автосохранения - не обновляем
      if (serverContent === currentContent) {
        return;
      }
      
      setFormData({
        title: post.title,
        content: post.content,
        type: post.type,
        status: post.status,
        image: post.image || '',
        scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '',
        category_ids: post.categories.map((cat: any) => cat.id),
        manual_created_at: post.manual_created_at ? new Date(post.manual_created_at).toISOString().slice(0, 10) : '',
        event_location: post.event_location || '',
        event_time: post.event_time || '',
        documents: post.documents || [],
      });
    }
  }, [postData, id]);

  // Эффективный id — либо параметр роута (если редактируем существующий),
  // либо id, полученный при первом createPost
  const getEffectiveId = (): string | null => {
    if (id && id !== 'new') return id;
    return createdPostIdRef.current;
  };

  // Тихая мутация для автосохранения (без уведомлений и обновления данных)
  const autoSaveMutation = useMutation(
    async (data: any) => {
      isAutoSavingRef.current = true;
      const effectiveId = getEffectiveId();
      if (effectiveId) {
        return apiClient.updatePost(effectiveId, data);
      }
      // Защита от гонок: пока первый createPost в полёте — не создаём новый пост
      if (isCreatingRef.current) {
        throw new Error('post-create-in-flight');
      }
      isCreatingRef.current = true;
      try {
        const response = await apiClient.createPost(data);
        if (response?.data?.id) {
          createdPostIdRef.current = response.data.id;
          window.history.replaceState(null, '', `/dashboard/posts/edit/${response.data.id}`);
        }
        return response;
      } finally {
        isCreatingRef.current = false;
      }
    },
    {
      onSuccess: () => {
        setIsDraftSaved(true);
        setTimeout(() => setIsDraftSaved(false), 2000);
        setTimeout(() => {
          isAutoSavingRef.current = false;
        }, 1000);
      },
      onError: (error: any) => {
        if (error?.message !== 'post-create-in-flight') {
          console.error('Auto-save error:', error);
        }
        isAutoSavingRef.current = false;
      },
    }
  );

  // Мутация для ручного сохранения поста (с уведомлениями)
  const saveMutation = useMutation(
    async (data: any) => {
      const effectiveId = getEffectiveId();
      if (effectiveId) {
        return apiClient.updatePost(effectiveId, data);
      }
      if (isCreatingRef.current) {
        throw new Error('post-create-in-flight');
      }
      isCreatingRef.current = true;
      try {
        const response = await apiClient.createPost(data);
        if (response?.data?.id) {
          createdPostIdRef.current = response.data.id;
        }
        return response;
      } finally {
        isCreatingRef.current = false;
      }
    },
    {
      onSuccess: (data) => {
        const wasUpdate = !!getEffectiveId() && getEffectiveId() !== data.data.id
          ? true
          : !!(id && id !== 'new');
        toast.success(wasUpdate ? 'Пост обновлен успешно' : 'Пост создан успешно');
        const effectiveId = getEffectiveId();
        if (effectiveId) {
          queryClient.invalidateQueries(['post', effectiveId]);
        }
        if (id === 'new' && data.data.id) {
          navigate(`/dashboard/posts/edit/${data.data.id}`);
        }
        setIsDraftSaved(true);
        setTimeout(() => setIsDraftSaved(false), 2000);
      },
      onError: (error: any) => {
        if (error?.message === 'post-create-in-flight') return;
        toast.error(error.response?.data?.error || 'Ошибка сохранения поста');
      },
    }
  );

  // Очистка таймаута при размонтировании
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  const handleSave = async () => {
    // Проверяем обязательные поля
    if (!formData.title.trim()) {
      toast.error('Заголовок обязателен');
      return;
    }
    
    if (!formData.content || formData.content === '<br>') {
      toast.error('Контент обязателен');
      return;
    }
    
    // Подготавливаем данные для отправки
    const postData = {
      title: formData.title,
      content: formData.content,
      type: formData.type,
      status: formData.status,
      image: formData.image,
      category_ids: formData.category_ids,
      scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : undefined,
      manual_created_at: formData.manual_created_at ? new Date(formData.manual_created_at).toISOString() : undefined,
      event_location: formData.event_location,
      event_time: formData.event_time,
      documents: formData.documents,
    };
    
    await saveMutation.mutateAsync(postData);
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    setIsImageUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await apiClient.uploadImage(formData);
      setFormData(prev => ({ ...prev, image: response.data.url }));
      debouncedAutoSave({ ...formData, image: response.data.url });
      toast.success('Изображение загружено успешно');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка загрузки изображения');
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-primary-500', 'bg-primary-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDocumentUpload = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setIsDocumentUploading(true);
    try {
      const uploaded: PostDocument[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = (files as any)[i] as File;
        const response = await apiClient.uploadDocument(file);
        uploaded.push({ url: response.data.url, label: response.data.label });
      }
      const newDocuments = [...formData.documents, ...uploaded];
      setFormData(prev => ({ ...prev, documents: newDocuments }));
      debouncedAutoSave({ ...formData, documents: newDocuments });
      toast.success(`Загружено документов: ${uploaded.length}`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка загрузки документа');
    } finally {
      setIsDocumentUploading(false);
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    }
  };

  const handleDocumentDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-primary-500', 'bg-primary-50');
  };

  const handleDocumentDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
  };

  const handleDocumentDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleDocumentUpload(files);
    }
  };

  const handleDocumentLabelChange = (index: number, label: string) => {
    const newDocuments = formData.documents.map((doc, i) =>
      i === index ? { ...doc, label } : doc
    );
    setFormData(prev => ({ ...prev, documents: newDocuments }));
    debouncedAutoSave({ ...formData, documents: newDocuments });
  };

  const handleDocumentRemove = (index: number) => {
    const newDocuments = formData.documents.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, documents: newDocuments }));
    debouncedAutoSave({ ...formData, documents: newDocuments });
  };

  if (isLoadingPost) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Загрузка поста...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/posts')}
                className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-lg font-medium text-gray-900">
                {id === 'new' ? 'Создать пост' : 'Редактировать пост'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              {isDraftSaved && (
                <span className="text-sm text-green-600 flex items-center">
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                  Черновик сохранен
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saveMutation.isLoading}
                className="btn btn-primary flex items-center"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {saveMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Заголовок
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setFormData({ ...formData, title: newTitle });
                  
                  // Debounce автосохранение
                  debouncedAutoSave({ ...formData, title: newTitle });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Введите заголовок поста..."
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-lg shadow">
              <RichTextEditor
                value={formData.content}
                onChange={(content) => {
                  setFormData({ ...formData, content });
                  
                  // Debounce автосохранение
                  debouncedAutoSave({ ...formData, content });
                }}
                placeholder="Начните писать контент..."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Post Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Настройки поста</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тип поста
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      const newType = e.target.value as any;
                      setFormData({ ...formData, type: newType });
                      
                      // Очищаем изображение для текстового типа
                      if (newType === 'texted') {
                        setFormData(prev => ({ ...prev, type: newType, image: '' }));
                        debouncedAutoSave({ ...formData, type: newType, image: '' });
                      } else {
                        setFormData(prev => ({ ...prev, type: newType }));
                        debouncedAutoSave({ ...formData, type: newType });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="detailed">Детально</option>
                    <option value="texted">Текст</option>
                    <option value="imaged">Изображение</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.type === 'detailed' && 'Полный пост с текстом и изображением'}
                    {formData.type === 'texted' && 'Только текст без изображения'}
                    {formData.type === 'imaged' && 'Пост с изображением и кратким текстом'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Статус
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => {
                      const newStatus = e.target.value as any;
                      setFormData({ ...formData, status: newStatus });
                      
                      // Очищаем запланированную дату если статус не "scheduled"
                      if (newStatus !== 'scheduled') {
                        setFormData(prev => ({ ...prev, status: newStatus, scheduled_at: '' }));
                        debouncedAutoSave({ ...formData, status: newStatus, scheduled_at: '' });
                      } else {
                        debouncedAutoSave({ ...formData, status: newStatus });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="draft">Черновик</option>
                    <option value="scheduled">Запланирован</option>
                    <option value="published">Опубликован</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Дата создания
                  </label>
                  <input
                    type="date"
                    value={formData.manual_created_at || ''}
                    onChange={(e) => {
                      const newManualCreatedAt = e.target.value;
                      setFormData(prev => ({ ...prev, manual_created_at: newManualCreatedAt }));
                      
                      // Debounce автосохранение
                      debouncedAutoSave({ ...formData, manual_created_at: newManualCreatedAt });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Дата создания поста (только дата, без времени)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Дата публикации
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => {
                      const newScheduledAt = e.target.value;
                      setFormData(prev => ({ ...prev, scheduled_at: newScheduledAt }));
                      
                      // Debounce автосохранение
                      debouncedAutoSave({ ...formData, scheduled_at: newScheduledAt });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Дата и время, когда пост будет опубликован
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Место проведения
                  </label>
                  <input
                    type="text"
                    value={formData.event_location}
                    onChange={(e) => {
                      const newEventLocation = e.target.value;
                      setFormData({ ...formData, event_location: newEventLocation });
                      debouncedAutoSave({ ...formData, event_location: newEventLocation });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Введите место проведения"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Дата проведения
                  </label>
                  <input
                    type="text"
                    value={formData.event_time}
                    onChange={(e) => {
                      const newEventTime = e.target.value;
                      setFormData({ ...formData, event_time: newEventTime });
                      debouncedAutoSave({ ...formData, event_time: newEventTime });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Например: 12 июня 2024, 18:00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Категории
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {categoriesData?.data.filter((category: any) => category.is_active).map((category: any) => (
                      <label key={category.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.category_ids.includes(category.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newCategoryIds = [...formData.category_ids, category.id];
                              setFormData({
                                ...formData,
                                category_ids: newCategoryIds
                              });
                              debouncedAutoSave({ ...formData, category_ids: newCategoryIds });
                            } else {
                              const newCategoryIds = formData.category_ids.filter(id => id !== category.id);
                              setFormData({
                                ...formData,
                                category_ids: newCategoryIds
                              });
                              debouncedAutoSave({ ...formData, category_ids: newCategoryIds });
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">{category.name}</span>
                        {category.is_default && (
                          <span className="ml-2 text-xs text-yellow-600">(по умолчанию)</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            {formData.type !== 'texted' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Изображение</h3>
                
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  {formData.image ? (
                    <div className="space-y-4">
                      <img
                        src={apiClient.getImageUrl(formData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                        onError={(e) => {
                          console.error('Failed to load image:', formData.image);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <button
                        onClick={() => {
                          setFormData({ ...formData, image: '' });
                          debouncedAutoSave({ ...formData, image: '' });
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Удалить изображение
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">
                          Перетащите изображение сюда или{' '}
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-primary-600 hover:text-primary-800 font-medium"
                          >
                            выберите файл
                          </button>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG до 5MB
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {isImageUploading && (
                  <div className="mt-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="text-sm text-gray-600 mt-2">Загрузка изображения...</p>
                  </div>
                )}
              </div>
            )}

            {/* Documents Upload */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Документы</h3>

              <div
                onDragOver={handleDocumentDragOver}
                onDragLeave={handleDocumentDragLeave}
                onDrop={handleDocumentDrop}
                onClick={() => documentInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer mb-4"
              >
                <DocumentArrowDownIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600 mt-2">
                  Перетащите документы сюда или нажмите для выбора
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, XLS, XLSX, PPT, TXT, ZIP и др. До 50MB
                </p>
              </div>

              <div className="space-y-3">
                {formData.documents.length === 0 && (
                  <p className="text-sm text-gray-500">Нет загруженных документов</p>
                )}
                {formData.documents.map((doc, index) => (
                  <div
                    key={`${doc.url}-${index}`}
                    className="flex items-start gap-2 p-3 border border-gray-200 rounded-md"
                  >
                    <DocumentIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-2" />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={doc.label}
                        onChange={(e) => handleDocumentLabelChange(index, e.target.value)}
                        placeholder="Название документа"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                      <a
                        href={apiClient.getMediaUrl(doc.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-1 text-xs text-primary-600 hover:text-primary-800 truncate"
                        title={doc.url}
                      >
                        {doc.url}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDocumentRemove(index)}
                      className="p-1 text-red-500 hover:text-red-700 flex-shrink-0"
                      title="Удалить"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {isDocumentUploading && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Загрузка документа...</p>
                </div>
              )}

              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.zip,.rar,.7z"
                onChange={(e) => handleDocumentUpload(e.target.files)}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostEditor; 